import { Autocomplete, Box, Slider, styled, TextField, type Theme } from '@mui/material';
import {
  FIBONACCI_SEQUENCE,
  type FibonacciFieldConfigurationDto,
  type FieldConfigurationDto,
} from '@tempo-io/fim3-dto';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FlexDiv } from './CustomFieldInput';
import { FIELD_DROPDOWN_MARGIN_RIGHT, FIELD_DROPDOWN_WIDTH } from './fieldSizingConstants';

const getFibonacciColor = (theme: Theme, value: number, maxValue: number) => {
  const ratio = value / maxValue;
  if (ratio >= 0.8) return theme.palette.success.main;
  if (ratio >= 0.5) return theme.palette.warning.main;
  if (ratio >= 0.2) return theme.palette.warning.light;
  return theme.palette.primary.main;
};

const FibonacciSlider = styled(Slider, {
  shouldForwardProp: (prop) => prop !== 'fibValue' && prop !== 'maxValue',
})<{ fibValue: number; maxValue: number }>(({ theme, fibValue, maxValue }) => ({
  color: getFibonacciColor(theme, fibValue, maxValue),
  height: 8,
  '& .MuiSlider-track': {
    border: 'none',
  },
  '& .MuiSlider-thumb': {
    height: 24,
    width: 24,
    backgroundColor: '#fff',
    border: '2px solid currentColor',
    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
      boxShadow: 'inherit',
    },
    '&::before': {
      display: 'none',
    },
  },
  '& .MuiSlider-mark': {
    backgroundColor: theme.palette.divider,
    height: 12,
    width: 2,
  },
  '& .MuiSlider-markActive': {
    backgroundColor: 'currentColor',
  },
  '& .MuiSlider-markLabel': {
    fontSize: '0.75rem',
  },
  '& .MuiSlider-valueLabel': {
    lineHeight: 1.2,
    fontSize: 12,
    background: 'unset',
    padding: 0,
    width: 32,
    height: 32,
    borderRadius: '50% 50% 50% 0',
    backgroundColor: getFibonacciColor(theme, fibValue, maxValue),
    transformOrigin: 'bottom left',
    transform: 'translate(50%, -100%) rotate(-45deg) scale(0)',
    '&::before': { display: 'none' },
    '&.MuiSlider-valueLabelOpen': {
      transform: 'translate(50%, -100%) rotate(-45deg) scale(1)',
    },
    '& > *': {
      transform: 'rotate(45deg)',
    },
  },
}));

export const FibonacciFieldInput = ({
  fieldConfiguration,
  values,
  setValues,
  isEditableText,
  useDropdown,
  required,
  ...rest
}: {
  fieldConfiguration: FieldConfigurationDto;
  values: string[];
  setValues: (values: string[]) => void;
  isEditableText?: boolean;
  useDropdown?: boolean;
  required?: boolean;
  autoFocus?: boolean;
}) => {
  const { t: a11y } = useTranslation('translation');

  if (fieldConfiguration.fieldType !== 'FIBONACCI') return null;

  const fibConfig = fieldConfiguration as FibonacciFieldConfigurationDto;
  const maxValue = fibConfig.maxValue;

  // Get valid Fibonacci numbers up to maxValue
  const validFibNumbers = FIBONACCI_SEQUENCE.filter((f) => f <= maxValue);

  const initializedRef = useRef(false);

  useEffect(() => {
    if (!initializedRef.current && (!values[0] || values[0].trim() === '')) {
      const defaultValue = validFibNumbers[0];
      if (defaultValue !== undefined && !useDropdown) {
        initializedRef.current = true;
        setValues([defaultValue.toString()]);
      }
    }
  }, [values, validFibNumbers, setValues, useDropdown]);

  // Add "None" option for optional fields when using dropdown
  const dropdownOptions = useDropdown && !required
    ? ['None', ...validFibNumbers.map(String)]
    : validFibNumbers.map(String);

  // Create marks for the slider
  const marks = validFibNumbers.map((fib, index) => ({
    value: index,
    label: fib.toString(),
  }));

  const hasValue = values[0] && values[0].trim() !== '';
  const currentValue = hasValue && values[0] ? parseInt(values[0], 10) : null;
  const currentIndex = currentValue !== null ? validFibNumbers.indexOf(currentValue as typeof validFibNumbers[number]) : -1;
  const displayIndex = currentIndex >= 0 ? currentIndex : 0;

  const handleSliderChange = (_event: Event, newIndex: number | number[]) => {
    const index = Array.isArray(newIndex) ? newIndex[0] : newIndex;
    if (index !== undefined) {
      const fibValue = validFibNumbers[index];
      if (fibValue !== undefined) {
        setValues([fibValue.toString()]);
      }
    }
  };

  const handleSliderInteraction = () => {
    if (!hasValue) {
      const defaultValue = validFibNumbers[displayIndex];
      if (defaultValue !== undefined) {
        setValues([defaultValue.toString()]);
      }
    }
  };

  const handleAutocompleteChange = (_event: React.SyntheticEvent, newValue: string | null) => {
    if (newValue === 'None' || newValue === null) {
      setValues(['']);
    } else {
      setValues([newValue]);
    }
  };

  // Show dropdown + slider together in card views
  if (useDropdown) {
    const displayValue = (values[0] || null) as string | undefined;

    return (
      <FlexDiv>
        <Autocomplete
          className="fs-exclude"
          data-dd-action-name="FIM 3 Custom Field - fibonacci autocomplete redacted"
          data-testid="fim3-custom-field-input-fibonacci-autocomplete"
          options={dropdownOptions}
          value={displayValue}
          inputValue={displayValue || ''}
          onChange={handleAutocompleteChange}
          onInputChange={(_event, newInputValue) => {
            // Handle manual typing in the input field
            if (newInputValue === '' || newInputValue === 'None') {
              setValues(['']);
            } else if (/^\d+$/.test(newInputValue)) {
              const numValue = parseInt(newInputValue, 10);
              if (!isNaN(numValue)) {
                const nearest = validFibNumbers.reduce((prev, curr) =>
                  Math.abs(curr - numValue) < Math.abs(prev - numValue) ? curr : prev
                );
                setValues([nearest.toString()]);
              }
            }
          }}
          size="small"
          disableClearable
          sx={{ width: FIELD_DROPDOWN_WIDTH, marginRight: FIELD_DROPDOWN_MARGIN_RIGHT }}
          renderInput={(params) => (
            <TextField
              {...params}
              slotProps={{
                input: {
                  ...params.InputProps,
                  'aria-label': a11y('ideaManagement.a11y.fibonacciField'),
                },
                htmlInput: {
                  ...params.inputProps,
                  'data-testid': 'fim3-custom-field-input-fibonacci-autocomplete-input',
                },
              }}
            />
          )}
          {...rest}
        />
        <FibonacciSlider
          className="fs-exclude"
          data-dd-action-name="FIM 3 Custom Field - fibonacci slider redacted"
          data-testid="fim3-custom-field-input-fibonacci-slider"
          aria-label={a11y('ideaManagement.a11y.fibonacciField')}
          value={displayIndex}
          onChange={handleSliderChange}
          onMouseDown={handleSliderInteraction}
          onTouchStart={handleSliderInteraction}
          min={0}
          max={validFibNumbers.length - 1}
          step={null}
          marks={marks}
          valueLabelDisplay="auto"
          valueLabelFormat={(index) => validFibNumbers[index]?.toString() ?? ''}
          fibValue={currentValue ?? validFibNumbers[0] ?? 1}
          maxValue={maxValue}
        />
      </FlexDiv>
    );
  }

  // Default: show slider only (inline editing, feedback forms, etc.)
  return (
    <Box sx={{ width: '100%', pl: 1, pr: 3, pt: 1 }}>
      <FibonacciSlider
        className="fs-exclude"
        data-dd-action-name="FIM 3 Custom Field - fibonacci slider redacted"
        data-testid="fim3-custom-field-input-fibonacci-slider"
        aria-label={a11y('ideaManagement.a11y.fibonacciField')}
        value={displayIndex}
        onChange={handleSliderChange}
        onMouseDown={handleSliderInteraction}
        onTouchStart={handleSliderInteraction}
        min={0}
        max={validFibNumbers.length - 1}
        step={null}
        marks={marks}
        valueLabelDisplay="auto"
        valueLabelFormat={(index) => validFibNumbers[index]?.toString() ?? ''}
        fibValue={currentValue ?? validFibNumbers[0] ?? 1}
        maxValue={maxValue}
        {...rest}
      />
    </Box>
  );
};
